/* eslint-disable no-lonely-if, no-else-return */
import { startTransition, useCallback } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import includes from "lodash-es/includes"
import isObject from "lodash-es/isObject"
import {
    type Location,
    type NavigateFunction,
    type SetURLSearchParams,
    useLocation,
    useNavigate,
    useSearchParams
} from "react-router-dom"
import { useMount, useShallowCompareEffect } from "react-use"

import { getIndexPage } from "$/utils/get-index-page"
import { setCurrentAccessToken, setCurrentRefreshToken } from "$/utils/tokens"

import { userQueryAdapter } from "@/3514/adapters"
import { PRODUCT_TYPES_WITH_ENROLLMENTS } from "@/3514/config"
import { EPath } from "@/3514/constants"
import { type TLogoutFunction, type TUserState, useIsNetworkFast, useLogout, useUser } from "@/3514/hooks"
import {
    assistantParticipantsPagePreloader,
    assistantParticipantsQueryPrefetcher
} from "@/3514/pages/assistant.participants"
import {
    coachDashboardAttendancesQueryPrefetcher,
    coachDashboardSessionsPrefetcher
} from "@/3514/pages/coach.dashboard"
import { participantCoachingSessionsPagePreloader } from "@/3514/pages/participant.coaching-sessions"
import { useParticipantOnboardingStepsLoginNavigator } from "@/3514/pages/participant.onboarding"
import { productTypeQueryPrefetcher } from "@/3514/queries"
import { type TAppDispatcher, useAppDispatcher } from "@/3514/store/app"
import {
    EProductTypeInternalName,
    type IUserCoachModel,
    type IUserDto,
    type IUserModel,
    type IUserParticipantModel
} from "@/3514/types"
import { isUserAssistant, isUserCoach, isUserManager, isUserParticipant } from "@/3514/utils"
import { CoachUrls, JS_CONF, ParticipantUrls } from "@/constants"
import { LocalStorageServiceKeys, StorageService } from "@/services"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"
import { matchingRouteByRole } from "@/utils/common"

import type { TAccountResponseDto, TPassMutationResponse } from "./auth.api.types"

const storageService: StorageService = new StorageService()

async function _populateTokens(user: IUserDto): Promise<void> {
    if ("access" in user.jwt_pair) setCurrentAccessToken(user.jwt_pair.access)

    if ("refresh" in user.jwt_pair) await setCurrentRefreshToken(user.jwt_pair.refresh)
}

type _TLoginNavigationFunction = (user?: IUserModel) => Promise<void>
function _useLoginNavigationFunction(): _TLoginNavigationFunction {
    const navigate: NavigateFunction = useNavigate()

    const location: Location = useLocation()

    const qc: QueryClient = useQueryClient()

    const onboardingNavigator: TEmptyAsyncCallback = useParticipantOnboardingStepsLoginNavigator()

    const isNetworkFast: boolean = useIsNetworkFast()

    return useCallback(
        async (user: IUserModel): Promise<void> => {
            const isCoach: boolean = isUserCoach(user) || isUserManager(user)
            const isParticipant: boolean = isUserParticipant(user)

            if (isUserAssistant(user)) {
                if (isNetworkFast) {
                    await assistantParticipantsQueryPrefetcher({ qc })

                    await assistantParticipantsPagePreloader()
                }

                await navigate(EPath.Assistant_Participants())
            }

            if (
                isParticipant &&
                (user as IUserParticipantModel).areNotCompletedSurveysPresent &&
                (user as IUserParticipantModel).isOnboardingFinished
            )
                await navigate(ParticipantUrls.SURVEYS)

            if (isParticipant && !(user as IUserParticipantModel).doesCohortExist)
                await navigate(ParticipantUrls.WAITING_LIST)

            if (location?.state?.from ? matchingRouteByRole(user.role, location?.state?.from) : false)
                await navigate(location?.state?.from)

            if (isCoach) {
                if ((user as IUserCoachModel).areSubUsersPresent)
                    await navigate(`${CoachUrls.WELCOME}${location?.state?.search ?? String()}`)
                else {
                    if (isNetworkFast) {
                        await coachDashboardAttendancesQueryPrefetcher({ qc })
                        await coachDashboardSessionsPrefetcher({ qc })
                    }

                    await navigate(EPath.Coach_Dashboard({ tab: "Sessions" }))
                }
            } else if (isParticipant)
                if ((user as IUserParticipantModel).isOnboardingFinished) {
                    if (isNetworkFast) await participantCoachingSessionsPagePreloader()

                    await navigate(
                        getIndexPage(
                            user,
                            includes<EProductTypeInternalName>(
                                PRODUCT_TYPES_WITH_ENROLLMENTS,
                                (user as IUserParticipantModel).productType
                            )
                        )
                    )
                } else if ((user as IUserParticipantModel).doesCohortExist) await onboardingNavigator()
        },
        [location?.state, isNetworkFast, onboardingNavigator, navigate, qc]
    )
}

type TPostLoginFunction = (dto: TAccountResponseDto) => Promise<void>
function usePostLoginFunction(): TPostLoginFunction {
    const loginNavigator: _TLoginNavigationFunction = _useLoginNavigationFunction()

    const { set: setUser }: TUserState = useUser()

    const qc: QueryClient = useQueryClient()

    const appDispatcher: TAppDispatcher = useAppDispatcher()

    return useCallback(
        async (dto: TAccountResponseDto): Promise<void> => {
            await _populateTokens(dto)

            await setUser(dto)

            const adapterUser: IUserModel = userQueryAdapter(dto)

            if (isUserCoach(adapterUser) || isUserManager(adapterUser)) appDispatcher.toggleSidebar()

            await productTypeQueryPrefetcher({ qc })

            await loginNavigator(adapterUser)
        },
        [loginNavigator, setUser, qc, appDispatcher]
    )
}

type TPostRegisterOrResetFunction = (resp: TPassMutationResponse) => Promise<void>
function usePostRegisterOrResetFunction(): TPostRegisterOrResetFunction {
    const { set: setUser }: TUserState = useUser()

    const qc: QueryClient = useQueryClient()

    const loginNavigator: _TLoginNavigationFunction = _useLoginNavigationFunction()

    return useCallback(
        async (resp: TPassMutationResponse): Promise<void> => {
            storageService.removeItem(LocalStorageServiceKeys.MainUserLoggedOut())

            if (isObject(resp.data)) {
                await _populateTokens(resp.data)

                await setUser(resp.data)

                const adaptedUser: IUserModel = userQueryAdapter(resp.data)

                await productTypeQueryPrefetcher({ qc })

                return loginNavigator(adaptedUser)
            }

            return void 0
        },
        [qc, loginNavigator, setUser]
    )
}

function useLoginMountNavigation(): void {
    const { user }: TUserState = useUser()

    const loginNavigator: _TLoginNavigationFunction = _useLoginNavigationFunction()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const resetErrParam: string = searchParams.get("isError")

    useShallowCompareEffect((): void => {
        if (!user || resetErrParam === "true") return

        startTransition(async (): Promise<void> => await loginNavigator(user))
    }, [user, resetErrParam])
}

function useForceNavigateToLogin(): void {
    const navigate: NavigateFunction = useNavigate()

    useMount((): void => {
        if (!JS_CONF.confirm_key || !JS_CONF.confirm_key_valid)
            navigate(EPath.Login({ isErrorReset: false }), { state: { isAlreadyRegistered: true }, replace: true })
    })
}

function useErrorLoginNavigator(): void {
    const logoutFunction: TLogoutFunction = useLogout()

    const { user } = useUser()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const resetErrParam: string = searchParams.get("isError")

    useShallowCompareEffect((): void => {
        if (resetErrParam === "true" && !!user) void logoutFunction({ onComplete: undefined })
    }, [resetErrParam, user, logoutFunction])
}

export {
    usePostLoginFunction,
    useLoginMountNavigation,
    usePostRegisterOrResetFunction,
    useForceNavigateToLogin,
    useErrorLoginNavigator
}
export type { TPostLoginFunction, TPostRegisterOrResetFunction }
